import * as THREE from 'three'
import type { ComposedAssemblyResult } from '../../loaders'
import { ImportedModelOccurrenceIndex } from '../occurrence'
import {
  ImportedModelInstancingEligibilityPolicy,
  ImportedModelRenderPrototypeBuilder,
} from '../prototype'
import {
  ImportedModelInstancedMeshRegistry,
  ImportedModelInstancedMeshUnit,
} from '../mesh-unit'
import {
  ImportedModelSourceMeshVisibilityController,
  importedModelSourceMeshExportBridge,
} from '../suppression'
import type { ImportedModelSourceMeshExportBridge } from '../suppression'
import {
  ImportedModelInstancedTopologyHostTransformProjector,
  ImportedModelTopologySourceLookupAnchorRegistry,
} from '../topology'
import { collectImportedModelTopologyRepositories } from '../../topology/imported-model-topology-runtime'
import { markCadObjectAsRuntimeDerived } from '../../../document'

/**
 * 导入模型 InstancedMesh 派生投影总协调器。
 *
 * Coordinator 只在模型语义、metadata、拓扑仓库和资源 scope 全部完成后工作。它先离屏
 * 构建并校验全部 Unit，再原子发布、注册命中映射并抑制 source Mesh；任何异常都会恢复
 * 普通 Mesh，使实例化永远不能破坏权威装配树与拓扑链。
 */
export class ImportedModelInstancedRenderCoordinator {
  /** 只读 prototype/slot 构建器。 */
  private readonly prototypeBuilder = new ImportedModelRenderPrototypeBuilder()

  /** 集中的实例化资格策略。 */
  private readonly eligibilityPolicy = new ImportedModelInstancingEligibilityPolicy()

  /** source Mesh 业务显隐与投影抑制控制器。 */
  private readonly visibilityController = new ImportedModelSourceMeshVisibilityController()

  /** 当前正式活动的投影。 */
  private activeProjection: PuppyCAD.ImportedModelInstancingActiveProjection | null = null

  /** 已脱离主场景树的 source Mesh 拓扑仓库反查锚点注册表。 */
  private readonly topologySourceLookupAnchorRegistry
    = new ImportedModelTopologySourceLookupAnchorRegistry()

  /** 临时外观 owner 到 source fallback occurrence 集合的绑定。 */
  private readonly temporarySourceFallbackByOwner = new Map<string, ReadonlySet<string>>()

  /** cad-core 领域 owner 到正式运行时 occurrence 贡献集合的绑定。 */
  private readonly runtimeOccurrencesByOwner = new Map<
    string,
    readonly PuppyCAD.ImportedModelInstancingOccurrence[]
  >()

  /** 最近一次激活输入中可由实例化层识别的稳定 occurrence 身份。 */
  private readonly knownOccurrenceIds = new Set<string>()

  /** 当前业务 occurrence transform 根到稳定身份的精确运行时索引。 */
  private readonly knownOccurrenceIdByRootObject = new Map<THREE.Object3D, string>()

  /** 当前业务 occurrence transform 根到共享 definition 身份的精确运行时索引。 */
  private readonly knownDefinitionKeyByRootObject = new Map<THREE.Object3D, string>()

  /** 把 topology pick host 接入活动 Unit 的端口实现。 */
  private readonly topologyHostTransformProjector: ImportedModelInstancedTopologyHostTransformProjector

  /** 最近一次加载挂载请求，供 metadata/显示状态最终收口后显式重试。 */
  private lastResult: ComposedAssemblyResult | null = null

  /** 最近一次加载挂载请求对应的 Viewer 场景。 */
  private lastScene: THREE.Scene | null = null

  /** 投影结构代次；重建后旧命中不能解释为新槽位。 */
  private generation = 0

  /** Coordinator 是否已经结束 Viewer 生命周期。 */
  private disposed = false

  /** 增量矩阵批次复用的投影父级世界逆矩阵。 */
  private readonly inverseProjectionParentMatrix = new THREE.Matrix4()

  /** 当前只读诊断统计。 */
  private stats: PuppyCAD.ImportedModelInstancingStats

  /**
   * 创建 Viewer 级实例化协调器。
   *
   * @param registry 当前 Viewer 独占的 InstancedMeshUnit 注册表。
   * @param mode 运行模式；正式产品加载默认启用，异常时仍自动回退普通 Mesh。
   */
  public constructor(
    private readonly registry: ImportedModelInstancedMeshRegistry,
    private readonly mode: PuppyCAD.ImportedModelInstancingMode = 'enabled',
    private readonly sourceMeshExportBridge: ImportedModelSourceMeshExportBridge
      = importedModelSourceMeshExportBridge,
  ) {
    this.topologyHostTransformProjector = new ImportedModelInstancedTopologyHostTransformProjector(
      registry,
    )
    this.stats = this.createEmptyStats('inactive')
  }

  /**
   * 在组合装配挂载完成后创建并发布实例化投影。
   *
   * @param result 已完成最终 occurrence 身份重绑定的组合装配结果。
   * @param scene 当前 Viewer 主场景。
   * @returns 当前导入结果对应的幂等生命周期句柄。
   */
  public activate(
    result: ComposedAssemblyResult,
    scene: THREE.Scene,
  ): PuppyCAD.ImportedModelInstancingRuntimeHandle {
    this.assertAvailable()
    const previousResult = this.lastResult
    if (previousResult && previousResult !== result) {
      // 项目或导入结果切换时，命令态临时外观不得泄漏到新 revision 的同名 occurrence。
      this.temporarySourceFallbackByOwner.clear()
      this.knownOccurrenceIds.clear()
      this.knownOccurrenceIdByRootObject.clear()
      this.knownDefinitionKeyByRootObject.clear()
      this.runtimeOccurrencesByOwner.clear()
    }
    this.releaseActiveProjection()
    if (previousResult?.instancingRuntime) {
      delete previousResult.instancingRuntime
    }
    this.generation += 1
    const activationGeneration = this.generation
    this.lastResult = result
    this.lastScene = scene
    const runtimeHandle = Object.freeze({
      dispose: (): void => this.deactivateGeneration(activationGeneration),
    })

    if (this.mode === 'off') {
      this.stats = this.createEmptyStats('inactive')
      result.instancingRuntime = runtimeHandle
      return runtimeHandle
    }

    const projectionParent = result.root.parent
    if (!projectionParent) {
      this.stats = this.createFallbackStats('装配结果尚未挂载到场景，无法建立实例投影')
      result.instancingRuntime = runtimeHandle
      return runtimeHandle
    }

    const runtimeOccurrences = this.collectRuntimeOccurrences()
    const occurrenceIndex = new ImportedModelOccurrenceIndex(
      result,
      runtimeOccurrences,
    )
    this.knownOccurrenceIds.clear()
    this.knownOccurrenceIdByRootObject.clear()
    this.knownDefinitionKeyByRootObject.clear()
    occurrenceIndex.listDefinitionKeys().forEach((definitionKey) => {
      occurrenceIndex.listOccurrencesByDefinition(definitionKey).forEach((occurrence) => {
        this.knownOccurrenceIds.add(occurrence.occurrenceId)
        this.knownOccurrenceIdByRootObject.set(occurrence.rootObject, occurrence.occurrenceId)
        this.knownDefinitionKeyByRootObject.set(occurrence.rootObject, definitionKey)
      })
    })
    const slots = this.prototypeBuilder.build(occurrenceIndex)
    const eligibleSlots: PuppyCAD.ImportedModelInstancingRenderSlot[] = []
    let rejectedSlotCount = 0
    for (const slot of slots) {
      const projectedSlot = this.excludeTemporarySourceFallbackMembers(slot)
      const eligibility = this.eligibilityPolicy.evaluate(projectedSlot)
      if (!eligibility.eligible) {
        rejectedSlotCount += 1
        continue
      }
      eligibleSlots.push(projectedSlot)
    }

    if (this.mode === 'diagnostic' || eligibleSlots.length === 0) {
      this.stats = Object.freeze({
        mode: this.mode,
        status: 'inactive',
        occurrenceCount: occurrenceIndex.getOccurrenceCount(),
        projectedSourceMeshCount: 0,
        instancedMeshCount: 0,
        savedDrawCallCount: 0,
        rejectedSlotCount,
        units: Object.freeze([]),
      })
      occurrenceIndex.dispose()
      result.instancingRuntime = runtimeHandle
      return runtimeHandle
    }

    const projectionRoot = new THREE.Group()
    projectionRoot.name = '导入模型实例化投影'
    projectionRoot.matrixAutoUpdate = false
    projectionRoot.matrix.identity()
    /** 正式 Unit 由导出桥接器投影为 occurrence 级实例快照，运行时多槽根本身不持久化。 */
    markCadObjectAsRuntimeDerived(projectionRoot)
    const units: ImportedModelInstancedMeshUnit[] = []
    const topologyRepositories = new Set(collectImportedModelTopologyRepositories(result.root))
    runtimeOccurrences.forEach((occurrence) => {
      collectImportedModelTopologyRepositories(occurrence.rootObject).forEach((repository) => {
        topologyRepositories.add(repository)
      })
    })
    let resourceLease: PuppyCAD.ImportedModelResourceScopeHandle | null = null

    try {
      // 共享 Geometry/Material 必须至少与全部 Unit 同寿命；额外 retain 复用原 scope 的同一注册表。
      resourceLease = result.resourceScope?.retain() ?? null
      for (const slot of eligibleSlots) {
        const unit = new ImportedModelInstancedMeshUnit(
          slot,
          projectionParent,
          activationGeneration,
          (sourceMesh, targetMatrix) => this.visibilityController.copyEffectiveWorldMatrix(
            sourceMesh,
            targetMatrix,
          ),
          sourceMesh => (
            this.visibilityController.resolveOriginalParent(sourceMesh) ?? sourceMesh.parent
          ),
        )
        projectionRoot.add(unit.object)
        units.push(unit)
      }

      // Unit 全部离屏构建成功后才把单一投影根加入场景，避免用户看到半发布状态。
      projectionParent.add(projectionRoot)
      this.sourceMeshExportBridge.register(projectionRoot, this.visibilityController, units)
      for (const unit of units) {
        this.registry.register(unit)
      }
      /**
       * 反向索引必须在 source Mesh 脱离主场景树前建立；索引之后只持有对象引用，不再依赖
       * source.parent，因此拖动、旋转与 Mate 增量更新不会重新扫描业务树。
       */
      const matrixBindingsByTransformObject = this.createMatrixBindingsByTransformObject(
        result,
        projectionParent,
        units,
      )
      /**
       * source Mesh 脱离父链前先登记精确仓库锚点；拓扑 hover 在实例命中回映射后仍以该
       * source Mesh 查询 Body 记录，不能依赖已经被移除的 parent 链。
       */
      this.topologySourceLookupAnchorRegistry.registerUnits(units)
      for (const unit of units) {
        const ownerId = unit.object.uuid
        for (const sourceMesh of unit.listSourceMeshes()) {
          this.visibilityController.suppress(sourceMesh, ownerId)
        }
      }
      /**
       * Registry 已完成发布后再向 Repository 注入端口：既有 host 会立即绑定，新建 host 则由
       * Repository 在 mount 时增量挂接，整个过程不扫描拖动热路径。
       */
      topologyRepositories.forEach((repository) => {
        repository.setTopologyHostTransformProjectionPort(
          this.topologyHostTransformProjector,
        )
      })
      const activeProjection = Object.freeze({
        result,
        scene,
        projectionParent,
        projectionRoot,
        occurrenceIndex,
        units: Object.freeze([...units]),
        matrixBindingsByTransformObject,
        topologyRepositories: Object.freeze([...topologyRepositories]),
        resourceLease,
        generation: activationGeneration,
        runtimeHandle,
      })
      this.activeProjection = activeProjection
      result.instancingRuntime = runtimeHandle
      const unitStats = Object.freeze(units.map(unit => unit.getStats()))
      const projectedSourceMeshCount = unitStats
        .reduce((total, unit) => total + unit.instanceCount, 0)
      this.stats = Object.freeze({
        mode: this.mode,
        status: 'active',
        occurrenceCount: occurrenceIndex.getOccurrenceCount(),
        projectedSourceMeshCount,
        instancedMeshCount: units.length,
        savedDrawCallCount: Math.max(0, projectedSourceMeshCount - units.length),
        rejectedSlotCount,
        units: unitStats,
      })
      return runtimeHandle
    } catch (error) {
      topologyRepositories.forEach((repository) => {
        repository.setTopologyHostTransformProjectionPort(null)
      })
      this.rollbackPublication(units, projectionRoot, resourceLease)
      occurrenceIndex.dispose()
      const message = error instanceof Error ? error.message : String(error)
      this.stats = this.createFallbackStats(`实例投影发布失败：${message}`)
      result.instancingRuntime = runtimeHandle
      return runtimeHandle
    }
  }

  /**
   * 使用当前组合结果重新建立实例投影。
   *
   * 材质或显示模式改变时必须新建 Unit，不能原地改写共享分组键；稳定 occurrence 身份仍从
   * 组合结果重新读取，GPU 下标则进入新的 generation。
   */
  public rebuild(): void {
    this.assertAvailable()
    const result = this.lastResult
    const scene = this.lastScene
    if (!result || !scene) {
      return
    }
    this.activate(result, scene)
  }

  /**
   * 替换一个 cad-core 领域 owner 当前贡献的正式运行时 occurrence，并原子重建实例投影。
   *
   * 运行时 occurrence 仍是业务 Object3D；本入口只让既有 Coordinator 把它们纳入统一
   * definition/slot 分桶，不持久化 GPU 下标，也不改变来源领域的历史数据。
   *
   * @param ownerToken 领域生命周期稳定 owner。
   * @param occurrences 当前 owner 的完整 occurrence 集合。
   */
  public replaceRuntimeOccurrences(
    ownerToken: string,
    occurrences: readonly PuppyCAD.ImportedModelInstancingOccurrence[],
  ): void {
    this.assertAvailable()
    const normalizedOwnerToken = ownerToken.trim()
    if (!normalizedOwnerToken) {
      throw new Error('实例化运行时 occurrence owner 不能为空')
    }

    if (occurrences.length === 0) {
      this.runtimeOccurrencesByOwner.delete(normalizedOwnerToken)
    } else {
      this.runtimeOccurrencesByOwner.set(
        normalizedOwnerToken,
        Object.freeze([...occurrences]),
      )
    }
    this.rebuild()
  }

  /**
   * 从当前活动 occurrence 索引解析对象所属的共享 definition 身份。
   *
   * @param object 组件根或其后代对象。
   * @returns 可供运行时派生 occurrence 复用的 definitionKey；无法解析时返回 null。
   */
  public resolveDefinitionKey(object: THREE.Object3D | null | undefined): string | null {
    let current = object ?? null
    while (current) {
      const definitionKey = this.knownDefinitionKeyByRootObject.get(current)
      if (definitionKey) {
        return definitionKey
      }
      current = current.parent
    }
    return null
  }

  /**
   * 直接更新指定变换对象影响到的实例矩阵槽。
   *
   * 反向索引在投影发布时一次建立；移动、旋转、Mate 和历史恢复只传入实际变化的组件根，
   * 本方法不会遍历其它 Unit、occurrence 或完整装配树。
   *
   * @param changedObjects 本轮发生局部或世界变换变化的组件对象。
   * @returns 实际写入 GPU 槽位的数量。
   */
  public updateObjectTransforms(changedObjects: readonly THREE.Object3D[]): number {
    const activeProjection = this.activeProjection
    if (!activeProjection || this.disposed || changedObjects.length === 0) {
      return 0
    }

    const uniqueChangedObjects = new Set(changedObjects)
    for (const object of uniqueChangedObjects) {
      if (this.hasChangedAncestor(object, uniqueChangedObjects)) {
        continue
      }
      object.updateWorldMatrix(true, true)
    }
    activeProjection.projectionParent.updateWorldMatrix(true, false)
    this.inverseProjectionParentMatrix.copy(activeProjection.projectionParent.matrixWorld).invert()

    const visitedBindings = new Set<PuppyCAD.ImportedModelInstancingMatrixBinding>()
    const changedUnits = new Set<ImportedModelInstancedMeshUnit>()
    let changedSlotCount = 0
    for (const object of uniqueChangedObjects) {
      const bindings = activeProjection.matrixBindingsByTransformObject.get(object) ?? []
      for (const binding of bindings) {
        if (visitedBindings.has(binding)) {
          continue
        }
        visitedBindings.add(binding)
        if (!binding.unit.syncSourceMeshTransform(
          binding.sourceMesh,
          this.inverseProjectionParentMatrix,
        )) {
          continue
        }
        changedUnits.add(binding.unit)
        changedSlotCount += 1
      }
    }
    changedUnits.forEach(unit => unit.commitMatrixChanges())
    return changedSlotCount
  }

  /**
   * 更新一个临时外观 owner 当前要求退出实例投影的 occurrence。
   *
   * 设计说明：
   * 1. Mate“使第一个选择透明”需要修改单个 occurrence 的材质，而 Unit 材质由同组实例共享；
   * 2. 首阶段采用确定性 source fallback：目标 occurrence 恢复普通 Mesh，其它仍满足资格的成员继续实例化；
   * 3. owner 切换只触发一次原子重建，清除后重新加入实例组；该状态不进入项目、历史或 Mate Definition。
   *
   * @param ownerToken 临时外观生命周期 owner。
   * @param occurrenceId 目标 occurrence；为空时清除该 owner。
   * @param occurrenceRoot 当前业务组件根，用于稳定 ID 编码不一致时精确回映射 occurrence。
   * @returns 实际改变当前投影成员集合时返回 true。
   */
  public setTemporarySourceFallback(
    ownerToken: string,
    occurrenceId: string | null,
    occurrenceRoot: THREE.Object3D | null = null,
  ): boolean {
    if (this.disposed) {
      return false
    }
    const normalizedOwnerToken = ownerToken.trim()
    const requestedOccurrenceId = occurrenceId?.trim() || null
    if (!normalizedOwnerToken) {
      return false
    }
    const normalizedOccurrenceIds = requestedOccurrenceId
      ? this.resolveKnownOccurrenceIds(requestedOccurrenceId, occurrenceRoot)
      : new Set<string>()
    if (requestedOccurrenceId && normalizedOccurrenceIds.size === 0) {
      return false
    }

    const previousEffectiveFallbacks = this.collectEffectiveTemporarySourceFallbacks()
    const previousOccurrenceIds = this.temporarySourceFallbackByOwner.get(normalizedOwnerToken)
      ?? new Set<string>()
    if (this.areStringSetsEqual(previousOccurrenceIds, normalizedOccurrenceIds)) {
      return false
    }

    if (normalizedOccurrenceIds.size > 0) {
      this.temporarySourceFallbackByOwner.set(
        normalizedOwnerToken,
        new Set(normalizedOccurrenceIds),
      )
    } else {
      this.temporarySourceFallbackByOwner.delete(normalizedOwnerToken)
    }
    const nextEffectiveFallbacks = this.collectEffectiveTemporarySourceFallbacks()
    if (this.areStringSetsEqual(previousEffectiveFallbacks, nextEffectiveFallbacks)) {
      return false
    }

    this.rebuild()
    return true
  }

  /** @returns 当前实例化统计快照。 */
  public getStats(): PuppyCAD.ImportedModelInstancingStats {
    return this.stats
  }

  /**
   * 读取 Viewer 级 topology host 实例变换投影端口。
   *
   * 阵列等独立实例领域只共享 source Mesh 到 Unit 的矩阵映射能力，不进入装配体 Coordinator
   * 的 occurrence、分桶或 Unit 生命周期。
   *
   * @returns 当前 Viewer 独占的 topology host 投影端口。
   */
  public getTopologyHostTransformProjectionPort(): ImportedModelInstancedTopologyHostTransformProjector {
    return this.topologyHostTransformProjector
  }

  /**
   * 读取对象在实例投影接管前的业务逻辑子节点。
   *
   * @param parentObject 待读取的业务父对象。
   * @returns 保持原兄弟顺序的逻辑子节点快照。
   */
  public listSourceHierarchyChildren(parentObject: THREE.Object3D): readonly THREE.Object3D[] {
    return this.visibilityController.listLogicalChildren(parentObject)
  }

  /**
   * 解析活动实例 source Mesh 脱离主场景树前的业务父对象。
   *
   * 移动与旋转必须继续在原父级局部坐标系内写入 source Mesh；物理 `parent=null` 只表示
   * 渲染投影已经接管显示职责，不能把业务组件误解释成世界根。
   *
   * @param sourceMesh 当前实例投影接管的业务 Mesh。
   * @returns 脱离前父对象；source Mesh 不属于当前投影时返回 null。
   */
  public resolveSourceMeshOriginalParent(sourceMesh: THREE.Mesh): THREE.Object3D | null {
    return this.visibilityController.resolveOriginalParent(sourceMesh)
  }

  /**
   * 在派生实例根临时移除、业务 source Mesh 临时恢复的同步窗口内执行场景操作。
   *
   * @param operation 依赖业务 Mesh 场景遍历的同步操作。
   * @returns 操作返回值。
   */
  public executeWithBusinessSourceMeshes<T>(operation: () => T): T {
    const scene = this.lastScene
    if (!scene || !this.activeProjection) {
      return operation()
    }
    return this.sourceMeshExportBridge.readBusinessScene(scene, operation)
  }

  /** 恢复普通 Mesh 并销毁当前活动投影。 */
  public deactivate(): void {
    const lastResult = this.lastResult
    this.releaseActiveProjection()
    if (lastResult?.instancingRuntime) {
      delete lastResult.instancingRuntime
    }
    this.lastResult = null
    this.lastScene = null
    this.temporarySourceFallbackByOwner.clear()
    this.runtimeOccurrencesByOwner.clear()
    this.knownOccurrenceIds.clear()
    this.knownOccurrenceIdByRootObject.clear()
    this.knownDefinitionKeyByRootObject.clear()
    this.stats = this.createEmptyStats('inactive')
  }

  /** 恢复普通 Mesh 并释放当前活动投影，但保留最近一次激活输入供原子重建。 */
  private releaseActiveProjection(): void {
    const activeProjection = this.activeProjection
    if (!activeProjection) {
      return
    }
    this.activeProjection = null
    this.sourceMeshExportBridge.unregister(activeProjection.projectionRoot)
    this.topologySourceLookupAnchorRegistry.clear()
    activeProjection.topologyRepositories.forEach((repository) => {
      repository.setTopologyHostTransformProjectionPort(null)
    })
    for (let index = activeProjection.units.length - 1; index >= 0; index -= 1) {
      const unit = activeProjection.units[index]
      if (!unit) {
        continue
      }
      this.visibilityController.releaseOwner(unit.object.uuid)
      this.registry.unregister(unit)
      unit.dispose()
    }
    activeProjection.projectionRoot.removeFromParent()
    activeProjection.projectionRoot.clear()
    activeProjection.occurrenceIndex.dispose()
    activeProjection.resourceLease?.release()
    if (activeProjection.result.instancingRuntime === activeProjection.runtimeHandle) {
      delete activeProjection.result.instancingRuntime
    }
  }

  /** 结束 Viewer 级协调器生命周期。 */
  public dispose(): void {
    if (this.disposed) {
      return
    }
    this.deactivate()
    this.visibilityController.dispose()
    this.registry.dispose()
    this.disposed = true
    this.stats = this.createEmptyStats('disposed')
  }

  /**
   * 收集全部领域 owner 当前贡献的运行时 occurrence。
   *
   * @returns 按 owner 与 occurrenceId 确定排序的只读集合。
   */
  private collectRuntimeOccurrences(): readonly PuppyCAD.ImportedModelInstancingOccurrence[] {
    return Object.freeze([...this.runtimeOccurrencesByOwner.entries()]
      .sort(([leftOwner], [rightOwner]) => leftOwner.localeCompare(rightOwner))
      .flatMap(([, occurrences]) => [...occurrences]
        .sort((left, right) => left.occurrenceId.localeCompare(right.occurrenceId))))
  }

  /**
   * 仅当句柄仍对应当前投影代次时执行销毁。
   *
   * @param generation 句柄创建时的结构代次。
   */
  private deactivateGeneration(generation: number): void {
    if (this.generation !== generation) {
      return
    }
    this.deactivate()
  }

  /**
   * 回滚尚未提交完整的发布事务。
   *
   * @param units 已创建 Unit。
   * @param projectionRoot 候选投影根。
   * @param resourceLease 已取得的共享资源 scope。
   */
  private rollbackPublication(
    units: readonly ImportedModelInstancedMeshUnit[],
    projectionRoot: THREE.Group,
    resourceLease: PuppyCAD.ImportedModelResourceScopeHandle | null,
  ): void {
    this.sourceMeshExportBridge.unregister(projectionRoot)
    this.topologySourceLookupAnchorRegistry.clear()
    for (let index = units.length - 1; index >= 0; index -= 1) {
      const unit = units[index]
      if (!unit) {
        continue
      }
      this.visibilityController.releaseOwner(unit.object.uuid)
      this.registry.unregister(unit)
      unit.dispose()
    }
    projectionRoot.removeFromParent()
    projectionRoot.clear()
    resourceLease?.release()
  }

  /**
   * 建立任一组件祖先对象到受影响实例槽的反向索引。
   *
   * source Mesh 自身及其到组合结果根之间的所有祖先都复用同一 binding 引用，因此调用方
   * 传入零件根、子装配根或具体 source Mesh 时都能定位相同 GPU 槽位，并可在同批次去重。
   *
   * @param result 当前组合装配结果。
   * @param projectionParent 实例投影与业务根的共同父对象。
   * @param units 已完成离屏构建的实例组。
   * @returns 只在当前投影 generation 内有效的反向索引。
   */
  private createMatrixBindingsByTransformObject(
    result: ComposedAssemblyResult,
    projectionParent: THREE.Object3D,
    units: readonly ImportedModelInstancedMeshUnit[],
  ): ReadonlyMap<THREE.Object3D, readonly PuppyCAD.ImportedModelInstancingMatrixBinding[]> {
    const mutableBindings = new Map<
      THREE.Object3D,
      PuppyCAD.ImportedModelInstancingMatrixBinding[]
    >()
    for (const unit of units) {
      for (const sourceMesh of unit.listSourceMeshes()) {
        const binding = Object.freeze({ unit, sourceMesh })
        let current: THREE.Object3D | null = sourceMesh
        while (current && current !== projectionParent) {
          const bindings = mutableBindings.get(current) ?? []
          bindings.push(binding)
          mutableBindings.set(current, bindings)
          if (current === result.root) {
            break
          }
          current = current.parent
        }
      }
    }

    return new Map([...mutableBindings.entries()].map(([object, bindings]) => [
      object,
      Object.freeze([...bindings]),
    ]))
  }

  /**
   * 判断同批变化集合中是否已有当前对象的祖先，避免重复刷新同一子树世界矩阵。
   *
   * @param object 当前变化对象。
   * @param changedObjects 本轮去重后的变化集合。
   * @returns 存在变化祖先时返回 true。
   */
  private hasChangedAncestor(
    object: THREE.Object3D,
    changedObjects: ReadonlySet<THREE.Object3D>,
  ): boolean {
    let current = object.parent
    while (current) {
      if (changedObjects.has(current)) {
        return true
      }
      current = current.parent
    }
    return false
  }

  /**
   * 从一个实例槽中移除当前临时 source fallback 的 occurrence 成员。
   *
   * @param slot 原始候选渲染槽。
   * @returns 可继续参与资格判定的渲染槽。
   */
  private excludeTemporarySourceFallbackMembers(
    slot: PuppyCAD.ImportedModelInstancingRenderSlot,
  ): PuppyCAD.ImportedModelInstancingRenderSlot {
    if (this.temporarySourceFallbackByOwner.size === 0) {
      return slot
    }
    const fallbackOccurrenceIds = this.collectEffectiveTemporarySourceFallbacks()
    const members = slot.members.filter((member) => !fallbackOccurrenceIds.has(member.occurrenceId))
    if (members.length === slot.members.length) {
      return slot
    }
    return Object.freeze({
      ...slot,
      members: Object.freeze(members),
    })
  }

  /**
   * 收集当前全部 owner 共同声明的有效 source fallback occurrence。
   *
   * @returns occurrence ID 集合。
   */
  private collectEffectiveTemporarySourceFallbacks(): ReadonlySet<string> {
    const fallbackOccurrenceIds = new Set<string>()
    this.temporarySourceFallbackByOwner.forEach((occurrenceIds) => {
      occurrenceIds.forEach((occurrenceId) => {
        fallbackOccurrenceIds.add(occurrenceId)
      })
    })
    return fallbackOccurrenceIds
  }

  /**
   * 比较两个字符串集合是否完全一致。
   *
   * @param left 左集合。
   * @param right 右集合。
   * @returns 集合成员完全一致时返回 true。
   */
  private areStringSetsEqual(
    left: ReadonlySet<string>,
    right: ReadonlySet<string>,
  ): boolean {
    return left.size === right.size && [...left].every((value) => right.has(value))
  }

  /**
   * 解析临时外观目标在当前投影 revision 中的精确 occurrence 身份。
   *
   * @param requestedOccurrenceId Mate 或装配命令携带的稳定组件 ID。
   * @param occurrenceRoot 当前运行时组件 transform 根。
   * @returns 当前组件子树中的实例化 occurrence ID 集合。
   */
  private resolveKnownOccurrenceIds(
    requestedOccurrenceId: string,
    occurrenceRoot: THREE.Object3D | null,
  ): ReadonlySet<string> {
    const occurrenceIds = new Set<string>()
    if (occurrenceRoot) {
      this.knownOccurrenceIdByRootObject.forEach((knownOccurrenceId, knownRootObject) => {
        if (this.isObjectInsideComponentRoot(knownRootObject, occurrenceRoot)) {
          occurrenceIds.add(knownOccurrenceId)
        }
      })
    }
    if (occurrenceIds.size === 0 && this.knownOccurrenceIds.has(requestedOccurrenceId)) {
      occurrenceIds.add(requestedOccurrenceId)
    }
    return occurrenceIds
  }

  /**
   * 判断一个 occurrence 根是否位于目标组件子树内。
   *
   * @param object 当前 occurrence transform 根。
   * @param componentRoot 临时外观目标组件根。
   * @returns 当前 occurrence 属于目标组件或其后代时返回 true。
   */
  private isObjectInsideComponentRoot(
    object: THREE.Object3D,
    componentRoot: THREE.Object3D,
  ): boolean {
    let current: THREE.Object3D | null = object
    while (current) {
      if (current === componentRoot) {
        return true
      }
      current = current.parent
    }
    return false
  }

  /**
   * 创建没有活动实例组的统计。
   *
   * @param status 当前生命周期状态。
   * @returns 冻结统计对象。
   */
  private createEmptyStats(
    status: PuppyCAD.ImportedModelInstancingStatus,
  ): PuppyCAD.ImportedModelInstancingStats {
    return Object.freeze({
      mode: this.mode,
      status,
      occurrenceCount: 0,
      projectedSourceMeshCount: 0,
      instancedMeshCount: 0,
      savedDrawCallCount: 0,
      rejectedSlotCount: 0,
      units: Object.freeze([]),
    })
  }

  /**
   * 创建整体降级统计。
   *
   * @param fallbackReason 中文失败上下文。
   * @returns 冻结统计对象。
   */
  private createFallbackStats(fallbackReason: string): PuppyCAD.ImportedModelInstancingStats {
    return Object.freeze({
      ...this.createEmptyStats('fallback'),
      fallbackReason,
    })
  }

  /** Viewer 销毁后禁止再次发布实例投影。 */
  private assertAvailable(): void {
    if (this.disposed) {
      throw new Error('导入模型实例化协调器已释放')
    }
  }
}
